import React from "react";
import { cn } from "../../lib/utils.js";
import { TONES, type StatusTone } from "../../lib/tones.js";
import { CATEGORICAL_TINTS, type CategoricalTint } from "../../lib/tints.js";
import { StatusDot } from "../StatusDot/index.js";
import type { StatusPillSize } from "./status-pill-variants.js";

/* StatusPill's leading marker (a dot, an icon or nothing), split out of
   StatusPill.tsx so the component stays under the 200-line cap. */

/* The marker slot's own box, keyed by size. Only `inline` needs one: `sm`,
   `md` and `lg` keep StatusDot's fixed px dot, and an icon there is sized by
   the caller (no consumer has asked for a forced icon size at those steps).
   At `inline` the wrapper is 1em square and forces a direct-child svg to fill
   it, so a caller's own fixed `h-4 w-4` icon still scales with the inherited
   font-size instead of clipping or floating loose inside the pill. */
const MARKER_WRAPPER_SIZE: Partial<Record<StatusPillSize, string>> = {
  inline: "h-[1em] w-[1em] [&>svg]:h-full [&>svg]:w-full",
};

/* StatusDot's step for the px sizes: `xs` (6px) through `md`, `sm` (8px) at
   `lg`. */
const PX_DOT_SIZE: Record<Exclude<StatusPillSize, "inline">, "xs" | "sm"> = {
  sm: "xs",
  md: "xs",
  lg: "sm",
};

/* StatusDot is fixed px (packages/ui/CLAUDE.md carve-out) and has no
   `bg-current`, so an `inline` pill (em-sized) and a solid pill draw their own
   dot at StatusDot's size for that step. */
const SELF_DOT_SIZE: Record<StatusPillSize, string> = {
  sm: "h-1.5 w-1.5",
  md: "h-1.5 w-1.5",
  lg: "h-2 w-2",
  inline: "h-[0.5em] w-[0.5em]",
};

export function renderMarker(
  tone: StatusTone,
  tint: CategoricalTint | undefined,
  onFill: boolean,
  size: StatusPillSize,
  icon: React.ReactNode | undefined,
  showDot: boolean,
): React.ReactNode {
  // showDot={false} is "no leading marker": it wins over the icon too.
  if (!showDot) return null;
  if (icon) {
    return (
      <span
        aria-hidden="true"
        className={cn(
          "inline-flex shrink-0 items-center justify-center",
          MARKER_WRAPPER_SIZE[size],
        )}
      >
        {icon}
      </span>
    );
  }
  const hue = tint === undefined ? { tone } : { tint };
  if (size !== "inline" && !onFill)
    return <StatusDot {...hue} size={PX_DOT_SIZE[size]} />;
  const colour = onFill
    ? "bg-current"
    : (tint === undefined ? TONES[tone] : CATEGORICAL_TINTS[tint]).dot;
  return (
    <span
      aria-hidden="true"
      className={cn("shrink-0 rounded-full", SELF_DOT_SIZE[size], colour)}
    />
  );
}
